/**
 * PaginatedTable — drop-in replacement for `<DataTable>` that talks to a
 * DRF endpoint paginated by `core.pagination.StudioPagination`.
 *
 * Backend contract (StudioPagination response):
 *   { count, num_pages, page, page_size, next, previous, results: T[] }
 *
 * Features:
 *   - Debounced search box (sends `?search=…` — handled globally by
 *     DRF's SearchFilter; per-viewset `search_fields` decides scope).
 *   - Optional status filter dropdown (`?status=…`).
 *   - Page-size selector: 25 / 50 / 100.
 *   - Prev / Next pagination with "Page x of y" indicator.
 *   - Loading skeleton + error box.
 *   - Refresh button (handy for runs that update server-side).
 *
 * It uses `rawFetch` so callers don't need typed openapi paths.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { rawFetch } from "../lib/api/client";
import { DataTable, EmptyState, ErrorBox } from "./common";

export interface PageResponse<T> {
  count: number;
  num_pages: number;
  page: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface Col<T> { header: string; cell: (row: T) => React.ReactNode; width?: string }

export interface PaginatedTableProps<T> {
  /** Endpoint path, NO query string — e.g. "/catalog/sources/". */
  endpoint: string;
  /** TanStack Query key prefix; defaults to ["table", endpoint]. */
  queryKey?: unknown[];
  /** Column definitions. */
  cols: Col<T>[];
  /** Empty-state message when count === 0. */
  empty?: string;
  /** Extra static query params (e.g. workspace=…). */
  baseParams?: Record<string, string | number | undefined | null>;
  /** Status filter options; omit to hide the dropdown. */
  statusOptions?: Array<{ label: string; value: string }>;
  /** Auto-refresh interval in ms (0/undefined disables). */
  refetchInterval?: number;
  /** Initial page size (default 25). */
  initialPageSize?: 25 | 50 | 100;
  /** Placeholder text for the search input. */
  searchPlaceholder?: string;
  /** Optional row styling for a specific result. */
  rowClassName?: (row: T) => string | undefined;
}

/** Cheap debouncer — avoids hitting the BE on every keystroke. */
function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function PaginatedTable<T extends { id?: string }>({
  endpoint, queryKey, cols, empty, baseParams,
  statusOptions, refetchInterval, initialPageSize = 25,
  searchPlaceholder = "Search…",
  rowClassName,
}: PaginatedTableProps<T>) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounced(search, 300);

  // Reset to page 1 whenever filters change (otherwise we may land on an
  // empty page and the user thinks the search returned zero results).
  useEffect(() => { setPage(1); }, [debouncedSearch, status, pageSize]);

  const params = useMemo(() => {
    const p: Record<string, string> = {
      page: String(page),
      page_size: String(pageSize),
    };
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    if (status) p.status = status;
    if (baseParams) {
      for (const [k, v] of Object.entries(baseParams)) {
        if (v !== undefined && v !== null && v !== "") p[k] = String(v);
      }
    }
    return p;
  }, [debouncedSearch, status, page, pageSize, baseParams]);

  const qs = new URLSearchParams(params).toString();
  const url = `${endpoint}${endpoint.includes("?") ? "&" : "?"}${qs}`;

  const { data, isLoading, isFetching, error, refetch } = useQuery<PageResponse<T>>({
    queryKey: [...(queryKey ?? ["table", endpoint]), params],
    queryFn: () => rawFetch<PageResponse<T>>(url),
    refetchInterval: refetchInterval || false,
    placeholderData: (prev) => prev,
  });

  const total = data?.count ?? 0;
  const numPages = data?.num_pages ?? 1;
  const rows = data?.results ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          className="input min-w-[14rem] flex-1"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {statusOptions && statusOptions.length > 0 && (
          <select className="input w-[10rem]"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
        <select className="input w-[7rem]"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}>
          <option value={25}>25 / page</option>
          <option value={50}>50 / page</option>
          <option value={100}>100 / page</option>
        </select>
        <button type="button" className="btn-ghost text-xs"
                onClick={() => refetch()}
                disabled={isFetching}>
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
        <span className="ml-auto text-xs text-muted">
          {isLoading ? "Loading…" : `${total.toLocaleString()} result${total === 1 ? "" : "s"}`}
        </span>
      </div>

      {error ? <ErrorBox error={error} /> : null}

      {isLoading ? (
        <div className="card p-6 text-sm text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState title={empty ?? "No rows yet."} />
      ) : (
        <DataTable rows={rows} cols={cols} rowClassName={rowClassName} />
      )}

      <div className="flex items-center justify-between text-xs text-muted">
        <span>
          Page {data?.page ?? page} of {numPages}
        </span>
        <div className="flex gap-2">
          <button type="button" className="btn-ghost"
                  disabled={!data?.previous || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ← Previous
          </button>
          <button type="button" className="btn-ghost"
                  disabled={!data?.next || isFetching}
                  onClick={() => setPage((p) => p + 1)}>
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
